import Components from "../../components";
import DateTimeHint from './date_time_hint';
import DateTimeRangeHint from './date_time_range_hint';
import SearchQuery from './search_query';
import SearchInput from './search_input';
import TokenRuler from './token_ruler';
import TokenHighlighter from './token_highlighter';
import TokenDeleter from './token_deleter';
import MenuHint from './menu_hint';
import RemoteMenuHint from './remote_menu_hint';
import StaticHint from './static_hint';

class FaultSearch extends Components.Base {
  initialize() {
    this.$input = this.$el.find('input.search-box');
    this.$form = this.$el.find('form');
    this.$hintContainer = this.$el.find('.hint-container');
    this.$hintHeader = this.$el.find('.hint-header');

    this.query = new SearchQuery();
    this.searchInput = new SearchInput(this.$input, this.query);

    // IE / Edge don't let us retreive the scroll position of text inputs.
    // Without it, we can't draw the highlights or delete button so don't try.
    if (
      !window.navigator.userAgent.match('Edge') &&
      !window.navigator.userAgent.match('.NET')
    ) {
      this.tokenRuler = new TokenRuler(this.$el.find('.invisible-text'));
      this.tokenHighlighter = new TokenHighlighter(
        this.$input,
        this.$el.find('.token-background'),
        this.tokenRuler,
        this.query,
      );
      this.tokenDeleter = new TokenDeleter(
        this.$input,
        this.tokenRuler,
        this.query,
        this.searchInput,
      );
    }

    this.hint = null;
    this.pattern = null;
    this.frozen = false;

    this.hintRules = [
      [
        /(^\s*occurred:)/,
        DateTimeRangeHint,
        {
          title: 'Search for any occurrences within range:',
          template: this.$el.find('#datetime-range-hint-template').html(),
        },
      ],
      [
        /(^\s*last_occurred\.before:)/,
        DateTimeHint,
        {
          title: 'Search for the latest occurrences before:',
          template: this.$el.find('#datetime-hint-template').html(),
        },
      ],
      [
        /(^\s*last_occurred\.after:)/,
        DateTimeHint,
        {
          title: 'Search for the latest occurrences after:',
          template: this.$el.find('#datetime-hint-template').html(),
        },
      ],
      [
        /(^\s*occurred\.before:)/,
        DateTimeHint,
        {
          title: 'Search for any occurrences before:',
          template: this.$el.find('#datetime-hint-template').html(),
        },
      ],
      [
        /(^\s*occurred\.after:)/,
        DateTimeHint,
        {
          title: 'Search for any occurrences after:',
          template: this.$el.find('#datetime-hint-template').html(),
        },
      ],
      [
        /(^\s*created\.before:)/,
        DateTimeHint,
        {
          title: 'Search for errors first seen before:',
          template: this.$el.find('#datetime-hint-template').html(),
        },
      ],
      [
        /(^\s*created\.after:)/,
        DateTimeHint,
        {
          title: 'Search for errors first seen after:',
          template: this.$el.find('#datetime-hint-template').html(),
        },
      ],
      [
        /(^assignee:)/,
        MenuHint,
        {
          title: 'Search Errors Assigned To:',
          template: this.$el.find('#assignee-hint-template').html(),
        },
      ],
      [
        /(^environment:)/,
        MenuHint,
        {
          title: 'Search Environment:',
          template: this.$el.find('#environment-hint-template').html(),
        },
      ],
    ];

    const autocompleteUrl = this.$el.data('autocomplete-url');

    if (autocompleteUrl) {
      this.hintRules = this.hintRules.concat([
        [
          /(^(?:request|params|context|session)\.[^:]*)/,
          RemoteMenuHint,
          {
            title: 'Autocomplete Nested Keys:',
            url: autocompleteUrl,
            template: this.$el.find('#remote-hint-template').html(),
          },
        ],
      ]);
    }
  }

  bind() {
    if (this.tokenHighlighter != null) {
      this.tokenHighlighter.bind();
    }
    if (this.tokenDeleter != null) {
      this.tokenDeleter.bind();
    }
    this.searchInput.bind();
    // Handle clicks on links like "Search Occurred Before A Date"
    this.on('click', '[data-search]', e => this.onClickShortcut(e));
    this.on('click', '.cancel-search', () => this.reset());
    this.on('click', '.back', () => this.back());

    // Monitor keyboard and mouse for events that should hide hints.
    this.on(this.$input, 'keydown', e => this.onKeyDown(e));
    this.on($(window), 'keydown', e => this.onKeyDownWindow(e));
    this.on($(window), 'click touchend', e => this.onClickWindow(e));

    // Update the hints if necessary when the query changes
    this.query.on('change', () => this.onSearchQueryChanged());
    this.searchInput.onChange();
  }

  unbind() {
    if (this.tokenHighlighter) {
      this.tokenHighlighter.unbind();
    }
    if (this.tokenDeleter) {
      this.tokenDeleter.unbind();
    }
    this.searchInput.unbind();
    this.query.off('change', this.onSearchQueryChanged);
  }

  onClickShortcut(e) {
    e.preventDefault();
    e.stopPropagation(); // Don't close hint when you click on shortcut links outside of container

    const remove = $(e.target).data('removes');
    if (remove && !e.shiftKey) {
      this.query.deleteTokensStartingWith(remove);
    }

    if ($(e.target).data('prepend') === 'always') {
      this.query.prepend($(e.target).data('search'));
    } else {
      this.frozen = !!$(e.target).data('prefilled');
      this.query.prependOrSelect($(e.target).data('search'));
      this.frozen = false;
    }
  }

  onKeyDown(e) {
    // Up or down. Always prevent default.
    if (e.keyCode === 38 || e.keyCode === 40) {
      if (this.hint && this.hint.keyPress) {
        this.hint.keyPress(e.keyCode);
      }
      e.preventDefault();
    }
    // Enter. Prevents default only if the hint explicitly tells it to
    if (e.keyCode === 13) {
      if (this.hint && this.hint.keyPress) {
        if (this.hint.keyPress(e.keyCode) === false) {
          e.preventDefault();
        }
      }
    }
    // Esc
    // Note: Esc will not be caught in FF
    if (e.keyCode === 27) {
      this.hideHint();
    }
  }

  onKeyDownWindow(e) {
    // Esc
    if (e.keyCode === 27) {
      this.hideHint();
    }
  }

  onClickWindow(e) {
    // If the user clicks outside of the search container, but not
    // on a pikaday element. This is because pikaday removes the target
    // from the dom, so the contians check always returns false.
    // We also hide if the search bookmark area is clicked

    let bookmarkClick = false;
    const bookmarkEl = this.$el.find('.c-search-bookmark')[0];
    if(bookmarkEl !== undefined) {
      bookmarkClick = $.contains(bookmarkEl, e.target);
    }

    if (
      (!$.contains(this.el, e.target) &&
      !e.target.className.toString().match('pika-')) || bookmarkClick
    ) {
      this.hideHint();
      this.$input.blur();
      this.$input.trigger('blur');
    }
  }

  onSearchQueryChanged() {
    if (this.$input.val() === '') {
      this.showDefaultHint();
      return;
    }

    if (!this.frozen && this.query.selectedToken) {
      const match = this.hintRules.find(item =>
        this.query.selectedToken.text.match(item[0]),
      );

      if (match) {
        const [pattern, klass, params] = match;
        this.showHintIfChanged(
          pattern,
          klass,
          this.query.selectedToken,
          this.query.tokens,
          params,
        );
        return;
      }
      this.showDefaultHint();
    }

    $('[data-search]').removeClass('active');
    this.query.tokens.forEach(t =>
      $(
        `[data-search="${t.text.replace(/"/g, '\\"')}"][data-prefilled]`,
      ).addClass('active'),
    );
  }

  showHintIfChanged(pattern, Klass, token, tokens, params) {
    if (pattern !== this.pattern) {
      if (this.hint != null) {
        this.hint.destroy();
      }
      this.hint = new Klass(
        this.$input,
        this.$hintContainer,
        token,
        params,
        this.query,
        this.searchInput,
      );
      this.pattern = pattern;
      if (params.root) {
        this.$hintHeader.find('.back').hide();
      } else {
        this.$hintHeader.find('.back').show();
      }

      this.$hintHeader.find('.title').html(params.title || '');
      this.$hintHeader.show();
    }
    if (this.hint) {
      this.hint.setToken(token);
    }
  }

  back() {
    this.showDefaultHint();
  }

  showDefaultHint() {
    this.showHintIfChanged(/(.+)/, StaticHint, null, [], {
      title: 'Select or type options to build a search query',
      root: true,
      template: this.$el.find('#default-hint-template').html(),
    });
  }

  reset() {
    this.hideHint();
  }

  hideHint() {
    this.query.trigger('change');
    if (this.hint != null) {
      this.hint.destroy();
    }
    this.$hintHeader.hide();
    this.$hintContainer.hide().empty();
    this.hint = null;
    this.pattern = null;
  }
}

Components.collection.register({
  selector: '.c-fault-search',
  klass: FaultSearch,
});

export default FaultSearch;
